import { useEffect, useRef, useState, type ReactNode } from "react";
import { Activity, Bell, Cable, CheckCheck, CheckCircle2, Cloud, CloudOff, Cpu, Download, FolderOpen, Info, Monitor, Settings2, Smartphone, TriangleAlert, X, Zap } from "lucide-react";
import { Badge, Button, Dialog, DialogActions } from "@studio";
import { fmtDateTime, fmtTime } from "../lib/format";
import { native, type LocalStatus } from "../lib/native";

/**
 * 데스크톱 하단 네이티브 상태바 + 실시간 알림 (시안 native-status / realtime-notifications 를 실제 값으로).
 * 값은 로컬 엔진 보고(window.metaCrewNative.getLocalStatus) 그대로이고, 모르는 값은 "확인 불가"로 둔다 (정상으로 추정하지 않음).
 * 알림은 의미 있는 상태 전이에서만 만든다 (하트비트마다 만들지 않음). 같은 종류는 묶어서 횟수로.
 */

type Tone = "ok" | "warn" | "error" | "neutral";
type Item = { id: string; label: string; value: string; detail: string; icon: ReactNode; tone: Tone; secondary?: boolean };

function statusItems(s: LocalStatus | null): Item[] {
  if (!s) {
    return [{ id: "engine", label: "엔진", value: "확인 불가", detail: "로컬 엔진 상태를 아직 받지 못했습니다.", icon: <Cpu />, tone: "neutral" }];
  }
  const engineDown = s.engine?.running === false || !!s.engineError;
  const offline = s.socket !== "connected";
  const approved = s.devices.filter((d) => d.state === "device").length;
  const pending = s.devices.filter((d) => d.state !== "device");
  const throttled = s.codex.throttledUntil && Date.parse(s.codex.throttledUntil) > Date.now();
  return [
    {
      id: "engine",
      label: "엔진",
      value: s.engine?.running === false ? "중지" : s.engineError ? "점검 필요" : "실행 중",
      detail: s.engineError ? `로컬 엔진 오류 · ${s.engineError}` : `백그라운드 엔진 정상${s.engine?.respondingAt ? ` · 마지막 확인 ${fmtTime(s.engine.respondingAt)}` : ""}`,
      icon: <Cpu />,
      tone: engineDown ? "error" : "ok",
    },
    {
      id: "connection",
      label: "코어",
      value: s.socket === "connected" ? "연결됨" : s.socket === "connecting" ? "재연결 중" : "끊김",
      detail: offline
        ? `마지막 수신 ${fmtTime(s.lastReceivedAt)} · 자동 재연결 중. 화면에는 마지막 수신 정보가 표시되고, 작업 이벤트는 이 PC에 보관됩니다.`
        : `중앙 소켓 연결됨 · 마지막 수신 ${fmtTime(s.lastReceivedAt)}`,
      icon: offline ? <CloudOff /> : <Cloud />,
      tone: offline ? "warn" : "ok",
    },
    {
      id: "devices",
      label: "디바이스",
      value: `${approved} / ${s.devices.length}`,
      detail: s.devices.length
        ? `USB 감지 ${s.devices.length}대 · 사용 가능 ${approved}대${pending.length ? ` · ${pending.map((d) => d.model ?? d.serial).join(", ")} 승인 대기. 디바이스에서 USB 디버깅 허용을 확인하세요.` : ""}`
        : "연결된 디바이스가 없습니다. USB 연결과 디버깅 설정을 확인하세요.",
      icon: <Smartphone />,
      tone: pending.length || !s.devices.length ? "warn" : "ok",
    },
    {
      id: "ai",
      label: "AI",
      value: s.codex.installed === false ? "설치 필요" : throttled ? "한도 도달" : s.codex.loggedIn ? "로그인됨" : "로그인 필요",
      detail: throttled
        ? `복구 예상 ${fmtDateTime(s.codex.throttledUntil)} · 이후 준비 상태를 다시 확인합니다.`
        : s.codex.loggedIn
          ? `${s.codex.version ? `Codex ${s.codex.version} · ` : ""}인증 확인 · 인증 토큰은 엔진에서 보관합니다.`
          : "이 PC 설정에서 AI 로그인을 진행하세요.",
      icon: <Zap />,
      tone: s.codex.loggedIn && !throttled ? "ok" : "warn",
      secondary: true,
    },
    {
      id: "outbox",
      label: "미전송",
      value: s.outboxPending === undefined ? "—" : `${s.outboxPending}건`,
      detail:
        s.outboxPending === undefined
          ? "이 프로그램 버전은 보관함 정보를 보고하지 않습니다."
          : s.outboxPending
            ? `이벤트 ${s.outboxPending}건을 로컬에 보관 중입니다. 연결이 복구되면 순서대로 전송합니다.`
            : "전송 대기 이벤트 없음",
      icon: <Cable />,
      tone: s.outboxPending ? "warn" : "neutral",
      secondary: true,
    },
  ];
}

/* ---------- 알림 ---------- */

type NoticeTone = "success" | "warning" | "error";
type Notice = { key: string; tone: NoticeTone; title: string; body: string; source: string; action: string; tab: string; count: number; read: boolean; time: string; revision: number };
type Draft = Omit<Notice, "count" | "read" | "time" | "revision">;

/** 직전 상태와 비교해 의미 있는 변화만 알림으로 만든다. */
function diff(prev: LocalStatus | null, next: LocalStatus, machine: string): Draft[] {
  const out: Draft[] = [];
  if (!prev) return out;
  if (prev.socket === "connected" && next.socket !== "connected")
    out.push({ key: "connection", tone: "warning", title: "코어 연결이 끊겼습니다", body: "자동으로 다시 연결합니다. 미전송 이벤트는 이 PC에 보관됩니다.", source: `연결 감시 · ${machine}`, action: "연결 확인", tab: "overview" });
  if (prev.socket !== "connected" && next.socket === "connected")
    out.push({
      key: "connection",
      tone: "success",
      title: "실시간 연결이 복구됐습니다",
      body: next.outboxPending !== undefined ? `보관 중인 이벤트 ${next.outboxPending}건을 전송합니다. 최신 상태를 다시 수신합니다.` : "최신 상태를 다시 수신합니다.",
      source: `연결 감시 · ${machine}`,
      action: "현황 보기",
      tab: "overview",
    });
  const prevPending = new Set(prev.devices.filter((d) => d.state !== "device").map((d) => d.serial));
  for (const d of next.devices.filter((x) => x.state !== "device" && !prevPending.has(x.serial)))
    out.push({ key: `device:${d.serial}`, tone: "warning", title: "디바이스 승인이 필요합니다", body: `${d.model ?? d.serial}에서 USB 디버깅을 허용해 주세요. 승인 전에는 작업에 사용할 수 없습니다.`, source: `디바이스 상태 · ${d.model ?? d.serial}`, action: "디바이스 확인", tab: "machine" });
  if (prev.codex.loggedIn && !next.codex.loggedIn)
    out.push({ key: "ai", tone: "warning", title: "AI 인증이 필요합니다", body: "AI 로그인이 풀렸습니다. 이 PC 설정에서 다시 로그인하세요. 그 전에는 작업을 받지 않습니다.", source: `AI 인증 · ${machine}`, action: "이 PC 설정", tab: "settings" });
  const wasThrottled = !!prev.codex.throttledUntil && Date.parse(prev.codex.throttledUntil) > Date.now();
  const isThrottled = !!next.codex.throttledUntil && Date.parse(next.codex.throttledUntil) > Date.now();
  if (!wasThrottled && isThrottled)
    out.push({ key: "ai-limit", tone: "warning", title: "AI 사용 한도에 도달했습니다", body: `복구 예상 ${fmtDateTime(next.codex.throttledUntil)}. 복구되면 자동으로 다시 작업을 받습니다.`, source: `AI 인증 · ${machine}`, action: "현황 보기", tab: "overview" });
  if (next.engineError && next.engineError !== prev.engineError)
    out.push({ key: "engine", tone: "error", title: "로컬 엔진에서 오류가 발생했습니다", body: next.engineError.slice(0, 200), source: `엔진 · ${machine}`, action: "이 PC 설정", tab: "settings" });
  return out;
}

export function DesktopStatusBar({ local, machineName, onNavigate }: { local: LocalStatus | null; machineName: string; onNavigate: (tab: string) => void }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [visible, setVisible] = useState<string[]>([]);
  const [inbox, setInbox] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const prev = useRef<LocalStatus | null>(null);
  const rev = useRef(0);

  useEffect(() => {
    if (!local) return;
    const drafts = diff(prev.current, local, machineName);
    prev.current = local;
    if (!drafts.length) return;
    const time = new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
    setNotices((all) => {
      let next = all;
      for (const d of drafts) {
        const before = next.find((n) => n.key === d.key);
        next = [{ ...d, count: (before?.count ?? 0) + 1, read: false, time, revision: ++rev.current }, ...next.filter((n) => n.key !== d.key)];
      }
      return next.slice(0, 50);
    });
    // 연결 복구가 끊김 토스트를 대체한다 (같은 key).
    setVisible((ids) => [...new Set([...drafts.map((d) => d.key), ...ids])].slice(0, 3));
    setAnnouncement(drafts.map((d) => d.title).join(" · "));
    // 창이 뒤에 있을 때는 OS 알림도 (경고·오류만).
    const n = native();
    for (const d of drafts) if (d.tone !== "success" && n?.notify && !document.hasFocus()) void n.notify(d.title, d.body);
  }, [local, machineName]);

  const items = statusItems(local);
  const current = items.find((i) => i.id === selected);
  const showAll = selected === "all" || selected === "version";
  const offline = local ? local.socket !== "connected" : false;
  const error = !!local?.engineError || local?.engine?.running === false;
  const unread = notices.filter((n) => !n.read).length;
  const upd = local?.update;
  const updating = upd?.state === "downloading";
  const visit = (n: Notice) => {
    setVisible((ids) => ids.filter((k) => k !== n.key));
    setNotices((all) => all.map((x) => (x.key === n.key ? { ...x, read: true } : x)));
    setInbox(false);
    onNavigate(n.tab);
  };
  return (
    <>
      <footer className={`lwn-statusbar ${offline ? "is-offline" : error ? "is-error" : ""}`} aria-label="브릿지 네이티브 상태">
        <div className="lwn-status-items">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`lwn-status-item lwn-${item.id} tone-${item.tone}${item.secondary ? " lwn-secondary" : ""}`}
              aria-label={`${item.label} ${item.value} · 상세 보기`}
              title={`${item.label} ${item.value} · ${item.detail}`}
              onClick={() => setSelected(item.id)}
            >
              {item.icon}
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </button>
          ))}
        </div>
        <div className="lwn-status-trailing">
          <button type="button" className="lwr-bell" aria-label={`알림 목록 · 읽지 않음 ${unread}건`} onClick={() => setInbox(true)}>
            <Bell size={14} />
            <span>알림</span>
            {unread > 0 && <b>{unread}</b>}
          </button>
          <button type="button" className={`lwn-version ${updating ? "is-updating" : ""}`} onClick={() => setSelected("version")} title="프로그램 버전과 업데이트 상세">
            {updating ? <Download size={13} /> : <Monitor size={13} />}
            <span>{updating ? `업데이트 ${upd?.progressPct ?? 0}%` : local?.appVersion ? `v${local.appVersion}` : "버전"}</span>
          </button>
          <button type="button" className="lwn-details-button" onClick={() => setSelected("all")} aria-label="네이티브 상태 전체 보기">
            <Activity size={14} />
            <span>로컬 상태</span>
          </button>
        </div>
      </footer>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <section className="lwr-toast-stack" aria-label="실시간 알림">
        {visible.map((key) => {
          const n = notices.find((x) => x.key === key);
          return n ? <Toast key={`${key}-${n.revision}`} notice={n} paused={inbox} onDismiss={() => setVisible((ids) => ids.filter((k) => k !== key))} onAction={() => visit(n)} /> : null;
        })}
      </section>
      <Dialog
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={current ? `${current.label} 상태` : "이 PC의 네이티브 상태"}
        description={`${machineName} · 로컬 엔진 보고`}
        size="wide"
        footer={
          <DialogActions
            secondary={{ label: "닫기", onClick: () => setSelected(null) }}
            primary={{
              label: "이 PC 설정 보기",
              onClick: () => {
                setSelected(null);
                onNavigate("settings");
              },
            }}
          />
        }
      >
        <div className="lwn-detail-heading">
          <span>
            <Monitor size={17} />
            {machineName}
          </span>
          <Badge tone={error ? "coral" : offline ? "amber" : "teal"}>{error ? "엔진 점검" : offline ? "오프라인 · 로컬 실행" : "로컬 엔진 실행 중"}</Badge>
        </div>
        <p className="lwn-detail-note">
          <Info size={14} />이 PC에서 확인한 상태입니다. 중앙 집계와 갱신 시점이 다를 수 있습니다.
        </p>
        <dl className="lwn-detail-list">
          {(showAll ? items : current ? [current] : []).map((item) => (
            <div key={item.id}>
              <dt className={`tone-${item.tone}`}>
                {item.icon}
                {item.label}
                <strong>{item.value}</strong>
              </dt>
              <dd>{item.detail}</dd>
            </div>
          ))}
        </dl>
        {showAll && (
          <section className="lwn-version-detail">
            <h3>
              <Download size={15} />
              프로그램 · 업데이트
            </h3>
            <p>
              {local?.appVersion ? `프로그램 v${local.appVersion} · ` : ""}네이티브 API v{native()?.version ?? "-"}
            </p>
            <p>{upd?.message ?? (upd?.state === "unsupported" ? "자동 업데이트를 지원하지 않는 버전입니다. 새 설치 파일로 업데이트하세요." : "업데이트 정보가 없습니다.")}</p>
          </section>
        )}
        <div className="lwn-detail-actions">
          <Button variant="secondary" size="sm" onClick={() => void native()?.openLogs()}>
            <FolderOpen size={14} />
            로그 폴더
          </Button>
          {showAll && (
            <Button variant="secondary" size="sm" disabled={offline} onClick={() => void native()?.checkUpdate()}>
              <Settings2 size={14} />
              업데이트 확인
            </Button>
          )}
        </div>
      </Dialog>
      <Dialog
        open={inbox}
        onOpenChange={setInbox}
        title="실시간 알림"
        description="이 PC의 상태 변화"
        size="wide"
        footer={
          <DialogActions
            secondary={{ label: "닫기", onClick: () => setInbox(false) }}
            primary={{
              label: "모두 읽음",
              disabled: !unread,
              onClick: () => {
                setNotices((all) => all.map((n) => ({ ...n, read: true })));
                setVisible([]);
              },
            }}
          />
        }
      >
        <div className="lwr-inbox-summary">
          <span>
            <Bell size={16} /> 읽지 않음 <strong>{unread}</strong>
          </span>
          <span>현재 세션 · {notices.length}개 항목</span>
        </div>
        {notices.length === 0 ? (
          <div className="lwr-empty">
            <CheckCheck size={28} />
            <strong>새로운 알림이 없습니다</strong>
            <p>상태 변화나 확인이 필요한 항목이 생기면 알려드립니다.</p>
          </div>
        ) : (
          <ul className="lwr-history">
            {notices.map((n) => (
              <li key={n.key} className={`tone-${n.tone} ${n.read ? "is-read" : ""}`}>
                <span className="lwr-notice-icon">
                  <NoticeIcon tone={n.tone} source={n.source} />
                </span>
                <div>
                  <div className="lwr-notice-meta">
                    <span>
                      {n.source} · {n.time}
                    </span>
                    <span>
                      {n.read ? "읽음" : "안 읽음"}
                      {n.count > 1 && ` · ${n.count}회`}
                    </span>
                  </div>
                  <strong>{n.title}</strong>
                  <p>{n.body}</p>
                  <Button variant="secondary" size="sm" onClick={() => visit(n)}>
                    {n.action}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </>
  );
}

function NoticeIcon({ tone, source }: { tone: NoticeTone; source: string }) {
  return tone === "success" ? <CheckCircle2 size={19} /> : tone === "error" ? <TriangleAlert size={19} /> : source.startsWith("연결") ? <CloudOff size={19} /> : <Info size={19} />;
}

function Toast({ notice, onDismiss, onAction, paused }: { notice: Notice; onDismiss: () => void; onAction: () => void; paused: boolean }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);
  // 성공 알림만 8초 뒤 닫힘 (마우스·포커스·목록 열림 동안 멈춤). 경고·오류는 직접 닫을 때까지.
  useEffect(() => {
    if (notice.tone !== "success" || paused || hovered || focused) return;
    const t = setTimeout(() => dismiss.current(), 8000);
    return () => clearTimeout(t);
  }, [notice.tone, notice.revision, paused, hovered, focused]);
  return (
    <article
      className={`lwr-toast tone-${notice.tone}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <span className="lwr-notice-icon">
        <NoticeIcon tone={notice.tone} source={notice.source} />
      </span>
      <div className="lwr-notice-content">
        <div className="lwr-notice-meta">
          <span>{notice.source}</span>
          <time>{notice.time}</time>
        </div>
        <strong>{notice.title}</strong>
        <p>{notice.body}</p>
        <div className="lwr-notice-actions">
          <button type="button" onClick={onAction}>
            {notice.action} <span aria-hidden="true">→</span>
          </button>
          <span>{notice.count > 1 ? `${notice.count}회 수신 · 묶음` : notice.tone === "success" ? "잠시 후 닫힘" : "조치 필요"}</span>
        </div>
      </div>
      <button type="button" className="lwr-close" aria-label={`${notice.title} 알림 닫기`} onClick={onDismiss}>
        <X size={15} />
      </button>
    </article>
  );
}
